import { useState, useRef } from 'react';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

interface FormState {
  status: 'idle' | 'loading' | 'success' | 'error';
  message: string;
}

export default function CTASection() {
  const groupRef = useScrollRevealGroup();
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', business: '', message: '' });
  const [state, setState] = useState<FormState>({ status: 'idle', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setState({ status: 'error', message: 'Please fill in all required fields.' });
      return;
    }
    if (form.message.length > 500) {
      setState({ status: 'error', message: 'Message must be 500 characters or less.' });
      return;
    }

    setState({ status: 'loading', message: '' });

    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));

      const res = await fetch('https://readdy.ai/api/form/d6v4sbu7ipaugi04lgc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setState({ status: 'success', message: "We've received your request. Our team will reach out within 24 hours." });
        setForm({ name: '', email: '', phone: '', business: '', message: '' });
        formRef.current?.reset();
      } else {
        setState({ status: 'error', message: 'Something went wrong. Please try again.' });
      }
    } catch {
      setState({ status: 'error', message: 'Connection error. Please try again.' });
    }
  };

  return (
    <section id="contact" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-20 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-ds-red/5 blur-[120px] rounded-full pointer-events-none" />

      <div
        className="relative max-w-7xl mx-auto px-6"
        ref={groupRef}
      >
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left copy */}
          <div>
            <span className="section-label reveal">Ready To Win?</span>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-none mb-6 reveal">
              OWN YOUR
              <br />
              <span className="text-ds-red neon-text-red">MARKET.</span>
            </h2>
            <p className="font-body text-white/60 text-lg leading-relaxed mb-8 reveal">
              Stop watching competitors outrank you. Get a free audit and a straight-talking strategy 
              session — no fluff, no hard sell. Just the truth about where you stand and exactly 
              what it takes to get on top.
            </p>

            <div className="space-y-4 reveal">
              {[
                { icon: 'ri-time-line', label: 'Free 30-min strategy call', color: 'text-ds-red' },
                { icon: 'ri-bar-chart-2-line', label: 'Full competitor analysis', color: 'text-ds-red' },
                { icon: 'ri-road-map-line', label: 'Custom growth roadmap', color: 'text-ds-red' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} ${item.color} text-base`} />
                  </div>
                  <span className="font-body text-white/80 text-sm">{item.label}</span>
                </div>
              ))}
            </div>

            {/* Divider line with glow */}
            <div className="mt-10 h-px w-32 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
            <p className="mt-4 font-body text-white/40 text-xs tracking-wider uppercase reveal">
              No contracts. No bullshit. Just results.
            </p>
            <a
              href="tel:1300029054"
              className="mt-6 inline-flex items-center gap-3 group reveal"
            >
              <div className="w-9 h-9 flex items-center justify-center border border-ds-red/40 group-hover:border-ds-red group-hover:bg-ds-red/10 transition-all duration-200">
                <i className="ri-phone-line text-ds-red text-base" />
              </div>
              <div>
                <p className="font-body text-white/40 text-xs tracking-widest uppercase">Call Us Direct</p>
                <p className="font-heading font-700 text-white text-lg tracking-wider group-hover:text-ds-red transition-colors duration-200">1300 029 054</p>
              </div>
            </a>
          </div>

          {/* Right form */}
          <div className="reveal">
            {state.status === 'success' ? (
              <div className="card-dark p-10 text-center flex flex-col items-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-ds-red/10 border border-ds-red/30">
                  <i className="ri-check-line text-ds-red text-3xl" />
                </div>
                <h3 className="font-display text-3xl text-white">REQUEST RECEIVED</h3>
                <p className="font-body text-white/60 text-sm leading-relaxed">{state.message}</p>
                <button
                  onClick={() => setState({ status: 'idle', message: '' })}
                  className="btn-outline mt-2 text-xs"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="card-dark p-8 space-y-4"
              >
                <h3 className="font-display text-2xl text-white tracking-wider mb-2">GET YOUR FREE AI VISIBILITY AUDIT</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/50 text-xs tracking-widest uppercase mb-1.5 font-body">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full bg-ds-bg-3 border border-ds-border text-white text-sm font-body px-4 py-3 outline-none focus:border-ds-red transition-colors duration-200 placeholder:text-white/20"
                    />
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs tracking-widest uppercase mb-1.5 font-body">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@business.com"
                      className="w-full bg-ds-bg-3 border border-ds-border text-white text-sm font-body px-4 py-3 outline-none focus:border-ds-red transition-colors duration-200 placeholder:text-white/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/50 text-xs tracking-widest uppercase mb-1.5 font-body">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="Your number"
                      className="w-full bg-ds-bg-3 border border-ds-border text-white text-sm font-body px-4 py-3 outline-none focus:border-ds-blue/50 transition-colors duration-200 placeholder:text-white/20"
                    />
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs tracking-widest uppercase mb-1.5 font-body">Business Type</label>
                    <select
                      name="business"
                      value={form.business}
                      onChange={handleChange}
                      className="w-full bg-ds-bg-3 border border-ds-border text-sm font-body px-4 py-3 outline-none focus:border-ds-blue/50 transition-colors duration-200 cursor-pointer"
                      style={{ color: form.business ? '#fff' : 'rgba(255,255,255,0.2)' }}
                    >
                      <option value="" disabled>Select industry</option>
                      <option value="trades" style={{ color: '#fff', background: '#111' }}>Trades</option>
                      <option value="hospitality" style={{ color: '#fff', background: '#111' }}>Hospitality</option>
                      <option value="retail" style={{ color: '#fff', background: '#111' }}>Retail</option>
                      <option value="health-wellness" style={{ color: '#fff', background: '#111' }}>Health &amp; Wellness</option>
                      <option value="professional" style={{ color: '#fff', background: '#111' }}>Professional Services</option>
                      <option value="other" style={{ color: '#fff', background: '#111' }}>Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-white/50 text-xs tracking-widest uppercase mb-1.5 font-body">
                    What&apos;s your biggest challenge? *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us what's holding your business back..."
                    rows={4}
                    maxLength={500}
                    className="w-full bg-ds-bg-3 border border-ds-border text-white text-sm font-body px-4 py-3 outline-none focus:border-ds-red transition-colors duration-200 placeholder:text-white/20 resize-none"
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-white/30 text-xs font-body">{form.message.length}/500</span>
                  </div>
                </div>

                {state.status === 'error' && (
                  <p className="text-ds-red text-xs font-body">{state.message}</p>
                )}

                <button
                  type="submit"
                  disabled={state.status === 'loading'}
                  className="btn-red w-full justify-center mt-2 text-sm"
                >
                  {state.status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Get Your Free AI Visibility Audit</span>
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}
